/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AccountAllocation,
  ChangePasswordData,
  SimplexQuoteData,
  UserOnboardingData,
  UserProfileType,
  UserSetting,
} from '@/types/user';
import axiosInstance from "./axiosInstance";
import { AxiosResponse } from 'axios';

export class UserService {
  private static readonly USER_ME_ENDPOINT = '/app/users/me';
  private static readonly USER_ME_UPDATE_ENDPOINT = '/app/users/me';
  private static readonly USER_GLOBAL_ASSETS_ALLOCATION_ENDPOINT = '/app/users/global-allocation'
  private static readonly USER_ONBOARDING_INFOS_ENDPOINT = '/app/users/onboarding/informations';
  private static readonly USER_FINANCIAL_ENDPOINT = '/app/users/onboarding/financial';
  private static readonly USER_LEGAL_ENDPOINT = '/app/users/onboarding/legal';
  private static readonly USER_SET_REFERRAL_ENDPOINT = '/app/users/onboarding/set-referral';
  private static readonly SIMPLEX_QUOTE_ENDPOINT = '/app/users/simplex/simplex-quote';
  private static readonly USER_CHANGE_PASSWORD_ENDPOINT = '/app/users/change-password';
  private static readonly USER_USERNAME_ENDPOINT = '/app/users/username';
  private static readonly USER_CHANGE_ADDRESS_ENDPOINT = '/app/users/address';
  private static readonly USER_PRESIGNED_URL_ENDPOINT = '/app/users/presigned-url';
  private static readonly USER_KYC_STATUS_ENDPOINT = '/app/users/kyc-status';
  private static readonly USER_REFERRALS_COUNT_ENDPOINT = '/app/users/referrals-count';
  private static readonly USER_ACTIVITY_LOG = '/app/users/activity-log';
  private static readonly USER_SETTING_ENDPOINT = '/app/users/setting'

  public static async getUser(): Promise<UserProfileType> {
    const res: AxiosResponse<UserProfileType> = await axiosInstance.get(UserService.USER_ME_ENDPOINT);
    return res.data;
  }

  public static async updateUser(data: Partial<UserProfileType>): Promise<UserProfileType> {
    const res: AxiosResponse<UserProfileType> = await axiosInstance.patch(UserService.USER_ME_UPDATE_ENDPOINT, data);
    return res.data;
  }

  public static async updateUserWelcome(data: Partial<UserProfileType>): Promise<UserProfileType> {
    const res: AxiosResponse<UserProfileType> = await axiosInstance.patch(UserService.USER_ME_UPDATE_ENDPOINT + '/welcome', data);
    return res.data;
  }

  public static async getGlobalAllocation(): Promise<AccountAllocation> {
    const res: AxiosResponse<AccountAllocation> = await axiosInstance.get(UserService.USER_GLOBAL_ASSETS_ALLOCATION_ENDPOINT);
    return res.data;
  }

  public static async getOnboardingInfos(): Promise<UserOnboardingData> {
    const res: AxiosResponse<UserOnboardingData> = await axiosInstance.get(UserService.USER_ONBOARDING_INFOS_ENDPOINT);
    return res.data;
  }

  public static async postUserFinancial(data: any): Promise<any> {
    const res: AxiosResponse<any> = await axiosInstance.post(UserService.USER_FINANCIAL_ENDPOINT, data);
    return res.data;
  }

  public static async postUserLegal(data: any): Promise<any> {
    const res: AxiosResponse<any> = await axiosInstance.post(UserService.USER_LEGAL_ENDPOINT, data);
    return res.data;
  }

  public static async postReferralCode(data: any): Promise<any> {
    const res: AxiosResponse<any> = await axiosInstance.post(UserService.USER_SET_REFERRAL_ENDPOINT, data);
    return res.data;
  }

  public static async getSimplexQuote(data: SimplexQuoteData): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.get(UserService.SIMPLEX_QUOTE_ENDPOINT, { params: data });
    return res.data;
  }

  public static async changePassword(data: ChangePasswordData): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.post(UserService.USER_CHANGE_PASSWORD_ENDPOINT, data);
    return res.data;
  }

  public static async changeUsername(data: any): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.post(UserService.USER_USERNAME_ENDPOINT, data);
    return res.data;
  }

  public static async changeAddress(data: any): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.post(UserService.USER_CHANGE_ADDRESS_ENDPOINT, data)
    return res.data;
  }

  public static async deleteUsername(): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.delete(UserService.USER_USERNAME_ENDPOINT);
    return res.data;
  }

  public static async getPresignedUrl(data: Record<string, any>): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.post(UserService.USER_PRESIGNED_URL_ENDPOINT, data);
    return res.data;
  }

  public static async getKycStatus(): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.get(UserService.USER_KYC_STATUS_ENDPOINT);
    return res.data;
  }

  public static async getReferralCount(): Promise<number> {
    const res: AxiosResponse<number> = await axiosInstance.get(UserService.USER_REFERRALS_COUNT_ENDPOINT);
    return res.data;
  }

  public static async activityLog(): Promise<Record<string, any>> {
    const res: AxiosResponse<Record<string, any>> = await axiosInstance.post(UserService.USER_ACTIVITY_LOG);
    return res.data;
  }

  public static async postSetting(setting: string, data: UserSetting): Promise<any> {
    const res: AxiosResponse<any> = await axiosInstance.post(UserService.USER_SETTING_ENDPOINT + '/' + setting, data);
    return res.data;
  }

  public static async getSetting(setting: string): Promise<any> {
    const res: AxiosResponse<string> = await axiosInstance.get(UserService.USER_SETTING_ENDPOINT + '/' + setting);
    return res.data;
  }
}
